import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Shield, Gauge, Wrench, Package, Wind, Award, TrendingUp, Ship, Navigation, Fuel, Container } from "lucide-react";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import StatsRibbon from "@/components/StatsRibbon";
import { Section, SectionLabel, SectionTitle, FadeIn } from "@/components/Section";
import vesselSunset from "@/assets/vessel-sunset.jpg";
import vesselAction1 from "@/assets/vessel-action-1.jpg";
import vesselAction2 from "@/assets/vessel-action-2.jpg";
import vesselAction3 from "@/assets/vessel-action-3.jpg";
import vesselAction4 from "@/assets/vessel-action-4.jpg";
import crewLifejackets from "@/assets/crew-lifejackets.jpg";
import passengerSleeping from "@/assets/passenger-sleeping.jpg";
import vesselRenderPort from "@/assets/vessel-render-port.jpg";
import vesselRenderStarboardBow from "@/assets/vessel-render-starboard-bow.jpg";
import vesselRenderStarboard from "@/assets/vessel-render-starboard.jpg";
import vesselRenderPortProfile from "@/assets/vessel-render-port-profile.jpg";
import vesselRenderStern from "@/assets/vessel-render-stern.jpg";
import vesselRenderStarboardStern from "@/assets/vessel-render-starboard-stern.jpg";
import vesselRenderBow from "@/assets/vessel-render-bow.jpg";
import vesselRenderTop from "@/assets/vessel-render-top.jpg";

const specs = [
  ["Builder", "AIRCAT Vessels SAS, France"],
  ["Length Overall", "35 metres"],
  ["Technology", "Air Cushion Catamaran"],
  ["Passenger Capacity", "80 PAX"],
  ["Service Speed", "46+ knots"],
  ["Range", "600+ NM"],
  ["Main Engines", "MTU Series 2000/4000"],
  ["Propulsion", "Kongsberg Kamewa Waterjets"],
  ["DP System", "Kongsberg K-Pos DP-21/22 (DPS-2)"],
  ["Classification", "ABS ✠A1 HSC, ✠AMS, ✠DPS-2"],
  ["Regulatory Compliance", "SOLAS / HSC Code"],
  ["Flag State", "Republic of the Marshall Islands"],
  ["Crew Complement", "5"],
  ["Navigation", "ECDIS, GPS, AIS, GMDSS A2/A3"],
];

const badges = ["ABS Classed", "DPS-2 Certified", "SOLAS / HSC Code", "IMO Tier II", "FLIR Equipped", "RMI Flagged"];

const galleryImages = [
  { img: vesselAction1, alt: "AIRCAT 35 underway with FPSO in background" },
  { img: vesselAction4, alt: "AIRCAT 35 at speed in turquoise waters" },
  { img: vesselAction2, alt: "AIRCAT 35 starboard profile at sea" },
  { img: vesselAction3, alt: "AIRCAT 35 aerial view near coastline" },
  { img: crewLifejackets, alt: "Offshore crew in coveralls and lifejackets on vessel deck" },
  { img: passengerSleeping, alt: "Premium blue leather reclining seats in crew cabin" },
];

const renderImages = [
  { img: vesselRenderStarboardBow, alt: "AIRCAT 35 starboard bow view" },
  { img: vesselRenderStarboard, alt: "AIRCAT 35 starboard profile" },
  { img: vesselRenderStarboardStern, alt: "AIRCAT 35 starboard stern view" },
  { img: vesselRenderStern, alt: "AIRCAT 35 stern quarter view" },
  { img: vesselRenderBow, alt: "AIRCAT 35 bow underside view" },
  { img: vesselRenderPort, alt: "AIRCAT 35 port side view" },
  { img: vesselRenderPortProfile, alt: "AIRCAT 35 port profile" },
  { img: vesselRenderTop, alt: "AIRCAT 35 top-down view" },
];

const crewlinerFeatures = [
  { icon: Gauge, text: "High transit speeds with reduced offshore personnel transfer times" },
  { icon: Wind, text: "Wave-piercing air-cushion hull for stability and passenger comfort in all weather conditions" },
  { icon: Shield, text: "Twin-engine redundancy and shallow draft for reliable, flexible port operations" },
  { icon: Wrench, text: "24/7 operational readiness underpinned by rigorous planned maintenance and proven reliability" },
];

const combiFeatures = [
  { icon: Package, text: "Crew transport and deck cargo in a single multi-purpose platform" },
  { icon: Wind, text: "Air cushion motion damping for safe transfers in a wider weather window" },
  { icon: Award, text: "ABS classed, DP2 optional" },
  { icon: TrendingUp, text: "Reduced operational footprint for clients managing integrated logistics" },
];

const psvCapabilities = [
  { icon: Container, text: "Deck cargo, bulk liquids, and consumables delivery to offshore installations" },
  { icon: Navigation, text: "DP-2 station-keeping for safe alongside operations in open sea conditions" },
  { icon: Fuel, text: "Fuel oil, potable water, and brine transfer capability" },
  { icon: Ship, text: "Experienced crew management and full technical operations under ISM Code" },
];

const Fleet = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [renderSlide, setRenderSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setRenderSlide((prev) => (prev + 1) % renderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
  <Layout>
    <PageHero
      tag="Fleet"
      title={<>AIRCAT <span className="accent-text">35 Crewliner</span></>}
      subtitle="Proven high-speed Crew Transfer Vessel technology delivering superior speed, seakeeping, and fuel efficiency in all weather conditions."
      backgroundImage={vesselSunset}
      backgroundVideo="/videos/fleet-bg.mp4"
    />
    <StatsRibbon stats={[
      { value: "35m", label: "Length" },
      { value: "80", label: "Passengers" },
      { value: "46+", label: "Knots" },
      { value: "DPS-2", label: "DP Class" },
      { value: "ABS", label: "Classification" },
    ]} />

    {/* OUR FLEET intro */}
    <Section>
      <div className="max-w-3xl">
        <SectionLabel>Our Fleet</SectionLabel>
        <SectionTitle>The right vessel, operated to the <span className="accent-text">highest standard</span></SectionTitle>
        <FadeIn>
          <p className="text-muted-foreground font-body text-lg leading-relaxed">
            Velocity Offshore deploys purpose-built, high-performance vessels selected for the specific demands of deepwater energy provinces. Our fleet is built around one principle: the right vessel, operated to the highest standard, where our clients need it most.
          </p>
        </FadeIn>
      </div>
    </Section>

    {/* Gallery */}
    <Section offWhite>
      <SectionLabel>Gallery</SectionLabel>
      <SectionTitle>Purpose-built for <span className="accent-text">crew transfer</span></SectionTitle>
      <FadeIn>
        <div className="relative w-full aspect-video sm:aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl shadow-navy/20 bg-navy-deep mt-8">
          <AnimatePresence initial={false}>
            <motion.img
              key={currentSlide}
              src={galleryImages[currentSlide].img}
              alt={galleryImages[currentSlide].alt}
              className="absolute inset-0 w-full h-full object-contain sm:object-cover"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 1.4, ease: [0.25, 0.1, 0.25, 1] }}
            />
          </AnimatePresence>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {galleryImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === currentSlide ? "bg-teal scale-125" : "bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>
      </FadeIn>
    </Section>

    {/* AIRCAT 35 Crewliner section */}
    <Section dark>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <FadeIn>
            <p className="text-teal/60 text-[10px] font-body tracking-[4px] uppercase mb-3">Air Cushion Catamaran</p>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-primary-foreground mb-6">
              AIRCAT 35 <span className="accent-text">Crewliner</span>
            </h2>
            <p className="text-primary-foreground/60 font-body text-base leading-relaxed mb-10">
              A high-speed aluminium Surface Effect Ship engineered for crew transfer in open ocean conditions. Fast, reliable, and built for back-to-back rotational schedules serving FPSOs, drillships, and fixed installations across the Stabroek Block and West Africa.
            </p>
          </FadeIn>
          <div className="space-y-5">
            {crewlinerFeatures.map((f, i) => (
              <FadeIn key={i} delay={0.1 * i}>
                <div className="flex items-start gap-4 group">
                  <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-lg bg-teal/10 border border-teal/20 flex items-center justify-center group-hover:bg-teal/20 transition-colors">
                    <f.icon className="w-5 h-5 text-teal" />
                  </div>
                  <p className="text-primary-foreground/75 font-body text-sm leading-relaxed pt-2">{f.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
        <FadeIn delay={0.2}>
          <div className="relative aspect-square rounded-2xl overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.img
                key={renderSlide}
                src={renderImages[renderSlide].img}
                alt={renderImages[renderSlide].alt}
                className="absolute inset-0 w-full h-full object-contain"
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 1.6, ease: [0.22, 0.6, 0.36, 1] }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 pointer-events-none z-[1]" style={{
              background: `
                radial-gradient(ellipse at center, transparent 25%, hsl(218 55% 10% / 0.6) 65%, hsl(218 55% 10%) 85%),
                linear-gradient(to bottom, hsl(218 55% 10% / 0.6) 0%, transparent 18%, transparent 55%, hsl(218 55% 10%) 90%),
                linear-gradient(to right, hsl(218 55% 10%) 0%, transparent 18%, transparent 82%, hsl(218 55% 10%) 100%)
              `
            }} />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {renderImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setRenderSlide(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i === renderSlide ? "bg-teal scale-125" : "bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>
        </FadeIn>
      </div>
    </Section>

    {/* Technical Specifications */}
    <Section offWhite>
      <div className="flex flex-wrap gap-2.5 mb-10">
        {badges.map((badge) => (
          <span
            key={badge}
            className="px-4 py-2 rounded-full text-[10px] font-bold tracking-[1.5px] uppercase font-body border border-teal/25 text-teal bg-teal/5"
          >
            {badge}
          </span>
        ))}
      </div>
      <SectionLabel>Technical Specifications</SectionLabel>
      <SectionTitle>Air Cushion <span className="accent-text">Catamaran</span></SectionTitle>
      <FadeIn>
        <div className="mt-4 grid sm:grid-cols-2 gap-x-12">
          {specs.map(([label, value]) => (
            <div key={label} className="flex justify-between py-3.5 border-b border-border/60 font-body text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-navy font-semibold">{value}</span>
            </div>
          ))}
        </div>
      </FadeIn>
    </Section>

    {/* AIRCAT 35 Combi */}
    <Section>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <FadeIn>
            <p className="text-teal text-[10px] font-body tracking-[4px] uppercase mb-3">Multi-Purpose Platform</p>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-navy mb-6">
              AIRCAT 35 <span className="accent-text">Combi</span>
            </h2>
            <p className="text-muted-foreground font-body text-base leading-relaxed mb-4">
              Where crew transfer meets cargo flexibility. The AIRCAT 35 Combi combines the speed and seakeeping of the AIRCAT SES platform with dedicated deck cargo capacity — delivering crew and critical supplies in a single, efficient run.
            </p>
            <p className="text-muted-foreground font-body text-base leading-relaxed mb-10">
              Ideal for operations where logistical consolidation reduces vessel movements and operational cost.
            </p>
          </FadeIn>
        </div>
        <div className="space-y-5">
          {combiFeatures.map((f, i) => (
            <FadeIn key={i} delay={0.1 * i}>
              <div className="flex items-start gap-4 group">
                <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-lg bg-teal/10 border border-teal/20 flex items-center justify-center group-hover:bg-teal/20 transition-colors">
                  <f.icon className="w-5 h-5 text-teal" />
                </div>
                <p className="text-foreground/75 font-body text-sm leading-relaxed pt-2">{f.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </Section>

    {/* Platform Supply Vessels */}
    <Section offWhite>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <FadeIn>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6">
              <Ship className="w-3.5 h-3.5 text-navy" />
              <span className="text-navy text-[10px] font-body font-bold tracking-[2px] uppercase">Expanded Capability</span>
            </div>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] tracking-tight text-navy mb-6">
              Platform Supply <span className="accent-text">Vessels</span>
            </h2>
            <p className="text-muted-foreground font-body text-base leading-relaxed mb-4">
              Beyond crew transfer, Velocity Offshore provides full technical and commercial management of Platform Supply Vessels — delivering integrated logistics support to offshore production facilities worldwide.
            </p>
            <p className="text-muted-foreground font-body text-base leading-relaxed mb-10">
              Our PSV operations follow the same operator-grade rigour and safety culture that define our CTV fleet, ensuring seamless supply chain reliability for our clients' offshore campaigns.
            </p>
          </FadeIn>
          <div className="space-y-5">
            {psvCapabilities.map((f, i) => (
              <FadeIn key={i} delay={0.1 * i}>
                <div className="flex items-start gap-4 group">
                  <div className="mt-0.5 flex-shrink-0 w-10 h-10 rounded-lg bg-navy/5 border border-navy/15 flex items-center justify-center group-hover:bg-navy/10 transition-colors">
                    <f.icon className="w-5 h-5 text-navy" />
                  </div>
                  <p className="text-foreground/75 font-body text-sm leading-relaxed pt-2">{f.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
        <FadeIn delay={0.2}>
          <div className="relative p-8 lg:p-12 rounded-2xl bg-navy text-center">
            <div className="absolute inset-0 rounded-2xl opacity-[0.04]" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, hsl(185 72% 42%) 0px, transparent 1px, transparent 40px)',
            }} />
            <div className="relative z-10">
              <Ship className="w-16 h-16 text-teal/40 mx-auto mb-6" />
              <h3 className="font-display text-2xl text-primary-foreground tracking-tight mb-3">PSV Operations</h3>
              <p className="text-primary-foreground/40 font-body text-sm leading-relaxed mb-8 max-w-sm mx-auto">
                Enquire about our PSV management capabilities and how we can support your offshore supply chain.
              </p>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2.5 px-7 py-3.5 text-[11px] font-bold tracking-[1.5px] uppercase rounded-full border border-teal/30 text-teal hover:bg-teal/10 transition-all duration-300 font-body"
              >
                Discuss PSV Requirements
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </FadeIn>
      </div>
    </Section>
    <Section dark className="relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'repeating-linear-gradient(90deg, hsl(185 72% 42%) 0px, transparent 1px, transparent 80px)',
      }} />
      <div className="relative z-10 text-center max-w-3xl mx-auto">
        <FadeIn>
          <SectionLabel>Fleet Expansion</SectionLabel>
          <h2 className="font-display text-[clamp(2rem,4.5vw,3.2rem)] leading-[1.1] tracking-tight text-primary-foreground mb-8">
            Scaling with <span className="accent-text">demand</span>
          </h2>
          <p className="text-primary-foreground/60 font-body text-lg leading-relaxed mb-6">
            Velocity Offshore is actively developing its fleet pipeline to meet growing demand across the Guyana-Suriname basin and West Africa deepwater corridor — ensuring our clients have scalable, reliable tonnage as their programmes grow.
          </p>
          <p className="text-primary-foreground/40 font-body text-base mb-10">
            Interested in upcoming capacity?
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2.5 px-8 py-4 text-[12px] font-bold tracking-[1.5px] uppercase rounded-full bg-gradient-to-r from-teal to-teal-light text-navy-deep hover:shadow-xl hover:shadow-teal/25 hover:scale-[1.02] transition-all duration-300 font-body"
          >
            Get in touch
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </div>
    </Section>
  </Layout>
  );
};

export default Fleet;
